import React, { useState, useEffect } from "react";
import adminService from "../../api/services/adminService";

const Notification = () => {
  const [activeTab, setActiveTab] = useState("send"); // "send" or "history"

  // Send Notification State
  const [newNotification, setNewNotification] = useState({
    title: "",
    message: "",
    userType: "", // 'premium', 'non-premium', or '' for all
    country: "", // country name or '' for all
    phoneVerified: false, // true = only unverified
    profileCompletion: false, // true = only < 70%
    lastActive: false, // true = only over a month
  });

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // History State
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [historyFilters, setHistoryFilters] = useState({
    type: "",
    startDate: "",
    endDate: "",
  });

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await adminService.getDistinctCountries();
        if (response.success && response.countries) {
          setCountries(response.countries);
        }
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch notification history
  const fetchNotificationHistory = async (page = 1, filters = {}) => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const params = {
        page: page,
        limit: 20,
        ...filters,
      };
      // Remove empty filter values
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null) {
          delete params[key];
        }
      });

      const response = await adminService.getNotificationHistory(params);
      if (response.success && response.notifications) {
        setNotificationHistory(response.notifications);
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
          setItemsPerPage(response.pagination.itemsPerPage);
        }
      }
    } catch (err) {
      console.error("Error fetching notification history:", err);
      setHistoryError(err?.message || "Failed to fetch notification history");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch history when tab changes to history, or when filters/page changes
  useEffect(() => {
    if (activeTab === "history") {
      fetchNotificationHistory(currentPage, historyFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    currentPage,
    historyFilters.type,
    historyFilters.startDate,
    historyFilters.endDate,
  ]);

  // Handle Create Notification - NEW API CALL with filters
  const handleCreateNotification = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    setLoading(true);

    const payload = {
      title: newNotification.title,
      message: newNotification.message,
      userType: newNotification.userType || null,
      country:
        newNotification.country === "all" || !newNotification.country
          ? null
          : newNotification.country,
      phoneVerified: newNotification.phoneVerified || null,
      profileCompletion: newNotification.profileCompletion || null,
      lastActive: newNotification.lastActive || null,
    };

    console.log("Creating filtered notification with payload:", payload);

    try {
      const response = await adminService.sendFilteredNotification(payload);
      console.log("Notification API response:", response);

      const successMessage = `Notification sent successfully! Delivered to ${
        response.totalUsers || 0
      } users (${response.successCount || 0} successful, ${
        response.failedCount || 0
      } failed).`;

      setSuccessMsg(successMessage);
      setNewNotification({
        title: "",
        message: "",
        userType: "",
        country: "",
        phoneVerified: false,
        profileCompletion: false,
        lastActive: false,
      });

      // Refresh history if on history tab
      if (activeTab === "history") {
        fetchNotificationHistory(currentPage, historyFilters);
      }
    } catch (err) {
      console.error("Create notification error:", err);

      if (err?.message && err?.message.includes("Network error")) {
        setError(
          "Network Error: Cannot connect to server. Please check your connection and try again."
        );
      } else if (err?.message) {
        setError(`Error: ${err?.message}`);
      } else if (err?.data) {
        setError(`Server Error: ${JSON?.stringify(err?.data)}`);
      } else {
        setError("Failed to send notification. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setHistoryFilters({
      ...historyFilters,
      [key]: value,
    });
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (historyFilters.type) count++;
    if (historyFilters.startDate) count++;
    if (historyFilters.endDate) count++;
    return count;
  };

  // Get filter display label
  const getFilterLabel = (key, value) => {
    if (key === "type") {
      const typeLabels = {
        VISIT: "Visit",
        COMPLIMENT: "Compliment",
        PROPOSAL: "Proposal",
        "Filtered User": "Filtered User",
        WELCOME: "Welcome",
      };
      return typeLabels[value] || value;
    }
    if (key === "startDate" || key === "endDate") {
      return new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return value;
  };

  // Remove a specific filter
  const removeFilter = (key) => {
    setHistoryFilters({
      ...historyFilters,
      [key]: "",
    });
    setCurrentPage(1);
  };

  return (
    <div className="p-4 w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Notifications</h2>
        {successMsg && activeTab === "send" && (
          <div className="text-green-700 bg-green-100 px-4 py-2 rounded-lg text-sm font-medium border border-green-200">
            {successMsg}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-4 bg-white rounded-xl shadow-md p-1 inline-flex">
        <button
          onClick={() => setActiveTab("send")}
          className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${
            activeTab === "send"
              ? "bg-red-500 text-white shadow-md"
              : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
          }`}>
          Send Notification
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${
            activeTab === "history"
              ? "bg-red-500 text-white shadow-md"
              : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
          }`}>
          History
          {activeTab === "history" && getActiveFiltersCount() > 0 && (
            <span className="ml-2 bg-white text-red-500 text-xs font-bold px-2 py-0.5 rounded-full">
              {getActiveFiltersCount()}
            </span>
          )}
        </button>
      </div>

      {/* Send Notification Tab */}
      {activeTab === "send" && (
        <>
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <div className="font-semibold">Error:</div>
                  <div>{error}</div>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-center">
              <div className="flex items-center justify-center gap-3">
                <svg
                  className="animate-spin h-5 w-5 text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="font-medium">Processing...</span>
              </div>
            </div>
          )}

          {/* Notification Form */}
          <div className="bg-white p-6 rounded-xl shadow-xl">
            <h3 className="text-xl font-bold text-gray-800 mb-6">
              Send Notification to Filtered Users
            </h3>

            <form onSubmit={handleCreateNotification} className="space-y-4">
              {/* Title and Message */}
              <div className="grid grid-cols-1 gap-4">
                {/* Title */}
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-semibold text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    id="title"
                    type="text"
                    value={newNotification.title}
                    onChange={(e) =>
                      setNewNotification({
                        ...newNotification,
                        title: e.target.value,
                      })
                    }
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                    placeholder="Enter notification title"
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    value={newNotification.message}
                    onChange={(e) =>
                      setNewNotification({
                        ...newNotification,
                        message: e.target.value,
                      })
                    }
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
                    placeholder="Enter your notification message here"
                    rows="4"
                    required
                  />
                </div>
              </div>

              {/* Filter Options */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h4 className="text-lg font-bold mb-4 text-gray-800">
                  Target Audience Filters
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* User Type (Premium/Non-Premium) */}
                  <div>
                    <label
                      htmlFor="userType"
                      className="block text-sm font-semibold text-gray-700 mb-2">
                      User Type
                    </label>
                    <select
                      id="userType"
                      value={newNotification.userType}
                      onChange={(e) =>
                        setNewNotification({
                          ...newNotification,
                          userType: e.target.value,
                        })
                      }
                      className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700">
                      <option value="">All Users</option>
                      <option value="premium">Premium Users</option>
                      <option value="non-premium">Non-Premium Users</option>
                    </select>
                  </div>

                  {/* Country */}
                  <div>
                    <label
                      htmlFor="country"
                      className="block text-sm font-semibold text-gray-700 mb-2">
                      Country
                    </label>
                    <select
                      id="country"
                      value={newNotification.country}
                      onChange={(e) =>
                        setNewNotification({
                          ...newNotification,
                          country: e.target.value,
                        })
                      }
                      disabled={loadingCountries}
                      className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700 disabled:bg-gray-100 disabled:cursor-not-allowed">
                      <option value="">All Countries</option>
                      {countries.map((country, index) => (
                        <option key={index} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    {loadingCountries && (
                      <p className="text-xs text-gray-500 mt-1.5 font-medium">
                        Loading countries...
                      </p>
                    )}
                  </div>
                </div>

                {/* Checkbox Filters */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="phoneVerified"
                      checked={newNotification.phoneVerified}
                      onChange={(e) =>
                        setNewNotification({
                          ...newNotification,
                          phoneVerified: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="phoneVerified"
                      className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                      Users without phone number verified
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="profileCompletion"
                      checked={newNotification.profileCompletion}
                      onChange={(e) =>
                        setNewNotification({
                          ...newNotification,
                          profileCompletion: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                    />
                    <label
                      htmlFor="profileCompletion"
                      className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                      Users with profile completion less than 70%
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="lastActive"
                      checked={newNotification.lastActive}
                      onChange={(e) =>
                        setNewNotification({
                          ...newNotification,
                          lastActive: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                    />
                    <label
                      htmlFor="lastActive"
                      className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                      Last active over a month ago
                    </label>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-red-500 w-64 text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:bg-red-600 hover:shadow-lg transition-all ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  }`}>
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    "Send Notification"
                  )}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <div className="bg-white rounded-xl shadow-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800">
              Notification History
            </h3>
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={() => {
                  setHistoryFilters({ type: "", startDate: "", endDate: "" });
                  setCurrentPage(1);
                }}
                className="text-sm text-red-600 hover:text-red-700 font-medium">
                Clear All Filters
              </button>
            )}
          </div>

          {/* Active Filters Display */}
          {getActiveFiltersCount() > 0 && (
            <div className="mb-4 flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm font-medium text-gray-700 self-center">
                Active Filters:
              </span>
              {historyFilters.type && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                  Type: {getFilterLabel("type", historyFilters.type)}
                  <button
                    onClick={() => removeFilter("type")}
                    className="hover:bg-red-200 rounded-full p-0.5 transition-colors">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {historyFilters.startDate && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  From: {getFilterLabel("startDate", historyFilters.startDate)}
                  <button
                    onClick={() => removeFilter("startDate")}
                    className="hover:bg-blue-200 rounded-full p-0.5 transition-colors">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
              {historyFilters.endDate && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  To: {getFilterLabel("endDate", historyFilters.endDate)}
                  <button
                    onClick={() => removeFilter("endDate")}
                    className="hover:bg-green-200 rounded-full p-0.5 transition-colors">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Type
              </label>
              <select
                value={historyFilters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700">
                <option value="">All Types</option>
                <option value="WELCOME">Welcome</option>
                <option value="VISIT">Visit</option>
                <option value="COMPLIMENT">Compliment</option>
                <option value="PROPOSAL">Proposal</option>
                <option value="Filtered User">Filtered User</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={historyFilters.startDate}
                onChange={(e) =>
                  handleFilterChange("startDate", e.target.value)
                }
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={historyFilters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700"
              />
            </div>
          </div>

          {/* Error Display */}
          {historyError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">{historyError}</span>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loadingHistory && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600 font-medium">
                Loading history...
              </p>
            </div>
          )}

          {/* History Table */}
          {!loadingHistory && (
            <>
              {notificationHistory.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">
                    No notifications found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {getActiveFiltersCount() > 0
                      ? "Try adjusting your filters"
                      : "No notification history available yet"}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 bg-white">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Date & Time
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Message
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Receiver
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {notificationHistory.map((notification) => (
                          <tr
                            key={notification.id}
                            className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatDate(notification.created_date)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                                {notification.type || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate font-medium">
                              {notification.title || "-"}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">
                              {notification.message || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {notification.receiverFirstName &&
                              notification.receiverLastName
                                ? `${notification.receiverFirstName} ${notification.receiverLastName}`
                                : notification.receiverEmail || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                                  notification.status === 0
                                    ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                    : "bg-green-100 text-green-800 border-green-200"
                                }`}>
                                {notification.status === 0 ? "Unread" : "Read"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-sm font-medium text-gray-700">
                        Showing page{" "}
                        <span className="font-bold text-gray-900">
                          {currentPage}
                        </span>{" "}
                        of{" "}
                        <span className="font-bold text-gray-900">
                          {totalPages}
                        </span>{" "}
                        (Total:{" "}
                        <span className="font-bold text-red-600">
                          {totalItems}
                        </span>{" "}
                        notifications)
                      </div>
                      <div className="flex gap-2 items-center justify-center">
                        <button
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1}
                          className={`px-4 py-2 border rounded-lg font-medium text-sm transition-colors ${
                            currentPage === 1
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                          }`}>
                          Previous
                        </button>
                        {/* <span className="px-3 py-1">
                          Page {currentPage}
                          {itemsPerPage} of {totalPages}
                        </span> */}
                        <span className="text-sm text-gray-700">
                          <span className="font-bold">
                            {Math.min(
                              (currentPage - 1) * itemsPerPage + 1,
                              totalItems
                            )}
                          </span>{" "}
                          -{" "}
                          <span className="font-bold">
                            {Math.min(currentPage * itemsPerPage, totalItems)}
                          </span>{" "}
                          of{" "}
                          <span className="font-bold text-red-600">
                            {totalPages}
                          </span>
                        </span>
                        <button
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(totalPages, prev + 1)
                            )
                          }
                          disabled={currentPage === totalPages}
                          className={`px-4 py-2 border rounded-lg font-medium text-sm transition-colors ${
                            currentPage === totalPages
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                          }`}>
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Notification;
